import { type FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ArrowTopIcon from '@assets/img/icons/arrow-top.svg?react';
import ChevronRightIcon from '@assets/img/icons/chevron-right-square.svg?react';
import { AppRoute } from '@common/app/routes';
import {
  useCheckPlantsCheck,
  useCheckRequirements,
} from '@common/hooks/check-requirements';
import { useIntroState } from '@common/hooks/intro-ref';
import { getImage } from '@common/lib/images';
import { getText } from '@common/lib/text';
import {
  type AvailableQuarry,
  type UserQuarry,
} from '@common/types/quarryType';
import { type ResourceTypeText } from '@common/types/resource';
import { ResourceStock } from '@components/ui/ResourceStock/ResourceStock';
import { Scale } from '@components/ui/Scale/Scale';
import { useAppSelector } from '@redux/store';
import cn from 'classnames';

import { resourceLevels } from '@/common/constants/levels';
import { convertRemToPixels } from '@/common/lib/convertRemtoPx';
import { LockResource } from '@/components/ui/LockResource/LockResource';
import { Picture } from '@/components/ui/Picture/Picture';

interface CardResourceLi {
  name: ResourceTypeText;
  data?: UserQuarry;
  nextToBuild?: AvailableQuarry;
}

const CardResourceLi: FC<CardResourceLi> = memo(
  ({ name, data, nextToBuild }) => {
    const { showAnimation } = useIntroState(3);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isComplete } = useCheckRequirements(
      nextToBuild?.creationRequirements,
    );
    const completePlants = useCheckPlantsCheck(
      nextToBuild?.creationRequirements?.plants,
    );

    const nextLevel = useCheckRequirements(data?.nextLevelRequirements);

    const image = getImage({ name, field: 'icon' });

    const title = getText({ name, kind: 'resource', t });

    const minLevel = resourceLevels[name];
    const userLevel = useAppSelector((state) => state.user.user?.level) || 0;

    if (userLevel < minLevel) {
      return <LockResource minLevel={minLevel} image={image} />;
    }

    return (
      <div
        key={name}
        className={cn(
          isComplete && nextToBuild?.name === name && 'cursor-pointer',
          showAnimation &&
            nextToBuild?.name === name &&
            'relative pointer-events-auto z-[11]',
        )}
        onClick={() => {
          console.log(name);

          if (data) {
            navigate(`${AppRoute.RESOURCES}/mineResource/${data.id}`);

            return;
          }

          if (nextToBuild?.id && nextToBuild.name === name && completePlants) {
            navigate(`${AppRoute.RESOURCES}/build`);
          }
        }}
      >
        {data || (nextToBuild?.name === name && completePlants) ? (
          <div className="card-resource">
            <div className="card-resource__content">
              <h6 className="card-resource__title">{title}</h6>
              {data ? (
                <>
                  <div className="card-resource__info">
                    <div className="card-resource__item quantity-item">
                      <div className="card-resource__quantity quantity-item__counter !text-white">
                        <ResourceStock
                          stock={data.localStock}
                          productionRate={data.productionRateOriginal}
                          limit={data.storageCapacity}
                          updatedAt={data.updatedAt}
                          plantId={data.id}
                        />
                        <Scale
                          className="mt-[0.375rem]"
                          height={convertRemToPixels(0.25)}
                          percentage={
                            data.localStock >= data.storageCapacity
                              ? 100
                              : data.storageCapacity
                                ? (data.localStock / data.storageCapacity) * 100
                                : 0
                          }
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <ArrowTopIcon
                  className={cn(
                    'w-4 h-4 shrink-0',
                    isComplete ? 'text-accent' : 'text-white/40',
                  )}
                />
              )}
            </div>
            {data && (
              <div className="card-resource__level">
                {data.level}
                {nextLevel.isComplete && (
                  <svg
                    id="icon-upgrade"
                    viewBox="0 0 32 32"
                    width={16}
                    style={{ margin: '-2px -4px -2px 0' }}
                  >
                    <path
                      fill="#82e024"
                      style={{ fill: 'var(--color1, #82e024)' }}
                      d="M16.687 5.66c-0.086-0.097-0.192-0.175-0.311-0.228s-0.247-0.081-0.377-0.081-0.259 0.028-0.377 0.081c-0.119 0.053-0.225 0.131-0.311 0.228l-8 9.778c-0.106 0.13-0.174 0.288-0.194 0.455s0.007 0.336 0.079 0.488c0.072 0.152 0.186 0.281 0.328 0.371s0.307 0.138 0.475 0.138h4.444v5.333c0 0.236 0.094 0.462 0.26 0.629s0.393 0.26 0.629 0.26h5.333c0.236 0 0.462-0.094 0.629-0.26s0.26-0.393 0.26-0.629v-5.333h4.445c0.168 0 0.333-0.048 0.475-0.138s0.256-0.218 0.328-0.371c0.072-0.152 0.099-0.321 0.079-0.488s-0.088-0.325-0.194-0.455l-8-9.778z"
                    ></path>
                    <path
                      fill="#82e024"
                      style={{ fill: 'var(--color1, #82e024)' }}
                      d="M18.666 24.889h-5.333c-0.236 0-0.462 0.094-0.629 0.26s-0.26 0.393-0.26 0.629c0 0.236 0.094 0.462 0.26 0.629s0.393 0.26 0.629 0.26h5.333c0.236 0 0.462-0.094 0.629-0.26s0.26-0.393 0.26-0.629c0-0.236-0.094-0.462-0.26-0.629s-0.393-0.26-0.629-0.26z"
                    ></path>
                  </svg>
                )}
              </div>
            )}
            <div className="card-resource__icon w-[4.5rem]">
              <Picture
                className="w-full"
                img={image?.pngRight || image?.img}
                webp={image?.webpRight || image?.webp}
                alt={name}
              />
              {/* <picture>
                <source
                  srcSet={image?.webpRight || image?.webp}
                  type="image/webp"
                />
                <img
                  width={72}
                  src={image?.pngRight || image?.img}
                  alt="icon"
                />
              </picture> */}
            </div>
            {(data || nextToBuild?.name === name) && (
              <ChevronRightIcon className="absolute w-5 h-5 right-[8px] bottom-[12px]" />
            )}
          </div>
        ) : (
          <LockResource image={image} minLevel={minLevel} />
        )}
      </div>
    );
  },
);

export default CardResourceLi;
